import { useEffect, useRef } from 'react';
import { createTooltip, showTooltip, hideTooltip } from '../utils.ts';

type ClientPos = { clientX: number; clientY: number };

type LocationLinesProps = {
  map: google.maps.Map | null;
  isLoaded: boolean;
  locationData?: any[];
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  onRightClick?: (item: any, pos: ClientPos) => void;
};

export function LocationLines({
  map,
  isLoaded,
  locationData,
  strokeColor = '#FFff00',
  strokeOpacity = 1,
  strokeWeight = 3,
  onRightClick
}: LocationLinesProps) {
  const onRightClickRef = useRef(onRightClick);
  useEffect(() => {
    onRightClickRef.current = onRightClick;
  }, [onRightClick]);

  useEffect(() => {
    if (!isLoaded || !map) return;
    if (!locationData?.length) return;

    const PIXEL_RADIUS = 6;

    const getRadius = (lat: number) => {
      const zoom = map.getZoom() ?? 10;
      const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
      return metersPerPixel * PIXEL_RADIUS;
    };

    const mapDiv = map.getDiv();
    const tooltip = createTooltip();
    mapDiv.appendChild(tooltip);

    const polylines: google.maps.Polyline[] = [];
    const circles: google.maps.Circle[] = [];
    const listeners: google.maps.MapsEventListener[] = [];

    locationData.forEach((item: any) => {
      const label: string | undefined = item.label;
      const tooltipRows = label ? [{ label: 'PTW No', value: label }] : [];

      item.lines?.forEach((line: [number, number][]) => {
        const path = line
          .filter(([lng, lat]) => Number.isFinite(lat) && Number.isFinite(lng))
          .map(([lng, lat]) => ({ lat, lng }));

        if (path.length < 2) return;

        // 시각적 대시 라인
        const polyline = new google.maps.Polyline({
          map,
          path,
          strokeOpacity: 0,
          strokeWeight: 0,
          geodesic: false,
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeColor,
                strokeOpacity,
                strokeWeight,
                scale: strokeWeight
              },
              offset: '0',
              repeat: '16px'
            }
          ]
        });

        // 이벤트 전용 투명 히트 영역
        const hitPolyline = new google.maps.Polyline({
          map,
          path,
          strokeColor,
          strokeOpacity: 0,
          strokeWeight: 12,
          geodesic: false
        });

        listeners.push(
          hitPolyline.addListener('click', (e: google.maps.MapMouseEvent) => {
            const me = e.domEvent as MouseEvent;
            me.preventDefault();
            onRightClickRef.current?.(item, { clientX: me.clientX, clientY: me.clientY });
          })
        );

        if (tooltipRows.length) {
          listeners.push(
            hitPolyline.addListener('mouseover', (e: google.maps.MapMouseEvent) => {
              const me = e.domEvent as MouseEvent;
              const rect = mapDiv.getBoundingClientRect();
              showTooltip(tooltip, tooltipRows, me.clientX - rect.left, me.clientY - rect.top);
            }),
            hitPolyline.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
              const me = e.domEvent as MouseEvent;
              const rect = mapDiv.getBoundingClientRect();
              showTooltip(tooltip, tooltipRows, me.clientX - rect.left, me.clientY - rect.top);
            }),
            hitPolyline.addListener('mouseout', () => hideTooltip(tooltip))
          );
        }

        polylines.push(polyline, hitPolyline);

        const makeCircle = (center: google.maps.LatLngLiteral) =>
          new google.maps.Circle({
            map,
            center,
            radius: getRadius(center.lat),
            strokeColor,
            strokeOpacity,
            strokeWeight: 2,
            fillColor: strokeColor,
            fillOpacity: 1
          });

        circles.push(makeCircle(path[0]));
        circles.push(makeCircle(path[path.length - 1]));
      });
    });

    const zoomListener = map.addListener('zoom_changed', () => {
      circles.forEach((c) => {
        const center = c.getCenter();
        if (center) c.setRadius(getRadius(center.lat()));
      });
    });

    return () => {
      polylines.forEach((p) => p.setMap(null));
      circles.forEach((c) => c.setMap(null));
      listeners.forEach((l) => l.remove());
      zoomListener.remove();
      tooltip.remove();
    };
  }, [map, isLoaded, locationData, strokeColor, strokeOpacity, strokeWeight]);

  return null;
}
